import type { Edge, Node, XYPosition } from '@xyflow/react';
import type { DatiNodo } from '../../components/email/automazioni/NodoPasso';
import { ID_INIZIO } from './disegnoSequenza';

/**
 * Dove stanno le caselle sulla lavagna: dove appoggiare quella nuova perche'
 * si veda subito, e come rimettere in ordine un disegno diventato confuso.
 * Solo geometria: niente database, niente React.
 */

/** Le misure della casella. NodoPasso le rispetta. */
export const LARGHEZZA_NODO = 240;
export const ALTEZZA_NODO = 92;

/** Lo spazio fra una casella e quella sotto, e fra due colonne affiancate. */
const PASSO_VERTICALE = 170;
const PASSO_ORIZZONTALE = LARGHEZZA_NODO + 60;

/** Fin dove si cerca un posto libero, in caselle di distanza da quello voluto. */
const RAGGIO_POSTO_LIBERO = 6;
/** Lo spazio che deve restare fra due caselle vicine. */
const MARGINE = 40;

/** Le uscite di ogni tipo di casella, nell'ordine in cui si riempiono. */
const USCITE: Record<DatiNodo['kind'], string[]> = {
  inizio: ['out'],
  invio: ['out'],
  attesa: ['out'],
  attendi_evento: ['arrivata', 'scaduta'],
  etichetta: ['out'],
  condizione: ['si', 'no'],
  ingresso_evento: ['out'],
  fine: [],
};

/** Le uscite che la casella ha davvero: senza tempo massimo l'attesa di una
 *  lista non scade, e "Scaduto" non c'e'. */
function usciteDi(nodo: Node<DatiNodo>): string[] {
  if (nodo.data.kind === 'attendi_evento' && !nodo.data.conScadenza) return ['arrivata'];
  return USCITE[nodo.data.kind];
}

/** Di quanto si sposta di lato la casella appesa a un ramo: a sinistra il si'
 *  e la lista arrivata, a destra il no e il tempo scaduto. */
const SCOSTAMENTO_RAMO: Record<string, number> = {
  out: 0,
  si: -PASSO_ORIZZONTALE / 2,
  no: PASSO_ORIZZONTALE / 2,
  arrivata: -PASSO_ORIZZONTALE / 2,
  scaduta: PASSO_ORIZZONTALE / 2,
};

/** I rami che vanno a destra: nell'ordine delle uscite vengono dopo. */
const RAMI_A_DESTRA = new Set(['no', 'scaduta']);

export interface Aggancio {
  /** La casella da cui parte la freccia verso quella nuova. */
  sorgente: string;
  /** L'attacco da cui parte: out, si oppure no. */
  uscita: string;
  posizione: XYPosition;
}

/** La prima uscita della casella da cui non parte ancora nessuna freccia. */
export function uscitaLibera(nodo: Node<DatiNodo>, archi: Edge[]): string | null {
  const occupate = new Set(
    archi.filter((a) => a.source === nodo.id).map((a) => a.sourceHandle ?? 'out')
  );
  return usciteDi(nodo).find((u) => !occupate.has(u)) ?? null;
}

function siSovrappone(posizione: XYPosition, nodi: Node[]): boolean {
  return nodi.some(
    (n) =>
      Math.abs(n.position.x - posizione.x) < LARGHEZZA_NODO + MARGINE / 2 &&
      Math.abs(n.position.y - posizione.y) < ALTEZZA_NODO + MARGINE
  );
}

/**
 * Gli spostamenti da provare attorno al posto voluto, dal piu' vicino al
 * piu' lontano. A parita' di distanza prima sotto, poi a destra: e' la
 * direzione in cui cresce un percorso.
 */
const SPOSTAMENTI: XYPosition[] = (() => {
  const celle: XYPosition[] = [];
  for (let dx = -RAGGIO_POSTO_LIBERO; dx <= RAGGIO_POSTO_LIBERO; dx++) {
    for (let dy = -RAGGIO_POSTO_LIBERO; dy <= RAGGIO_POSTO_LIBERO; dy++) {
      celle.push({ x: dx, y: dy });
    }
  }
  const distanza = (c: XYPosition) => c.x * c.x + c.y * c.y;
  return celle
    .sort((a, b) => distanza(a) - distanza(b) || b.y - a.y || b.x - a.x)
    .map((c) => ({ x: c.x * PASSO_ORIZZONTALE, y: c.y * (ALTEZZA_NODO + MARGINE) }));
})();

/**
 * Il posto libero piu' vicino a quello voluto, cercando tutto intorno: una
 * casella nuova non finisce mai sotto un'altra, dove non la si vedrebbe.
 */
export function posizioneLibera(voluta: XYPosition, nodi: Node[]): XYPosition {
  for (const spostamento of SPOSTAMENTI) {
    const posizione = { x: voluta.x + spostamento.x, y: voluta.y + spostamento.y };
    if (!siSovrappone(posizione, nodi)) return posizione;
  }
  return voluta;
}

/**
 * Dove si attacca una casella appena aggiunta.
 *
 * Se c'e' una casella scelta con un'uscita libera, la nuova va sotto di lei
 * ed e' gia' collegata: e' quasi sempre il passo successivo che si vuole. Se
 * la sequenza e' ancora vuota, la prima casella si attacca a "Si parte".
 * Negli altri casi null: la casella va dove si sta guardando, scollegata.
 */
export function aggancio(
  sceltoId: string | null,
  nodi: Node<DatiNodo>[],
  archi: Edge[]
): Aggancio | null {
  const partenzaLibera = !archi.some((a) => a.source === ID_INIZIO);
  const candidato =
    nodi.find((n) => n.id === sceltoId) ??
    (partenzaLibera ? nodi.find((n) => n.id === ID_INIZIO) : undefined);
  if (!candidato) return null;

  const uscita = uscitaLibera(candidato, archi);
  if (!uscita) return null;

  const voluta = {
    x: candidato.position.x + SCOSTAMENTO_RAMO[uscita],
    y: candidato.position.y + PASSO_VERTICALE,
  };
  return { sorgente: candidato.id, uscita, posizione: posizioneLibera(voluta, nodi) };
}

/**
 * Rimette in ordine il disegno: dall'alto in basso, ogni casella sotto
 * quella che la precede, il si' a sinistra e il no a destra (e cosi' la lista
 * arrivata e il tempo scaduto).
 *
 * L'altezza di una casella e' il percorso piu' lungo per arrivarci, cosi'
 * dove due rami si riuniscono la casella sta sotto a entrambi. La colonna
 * invece la decide chi la raggiunge per primo: il no che salta un pezzo del
 * si' resta a destra, nella sua colonna. I rami "ogni volta che entra in una
 * lista" e le caselle non collegate finiscono a destra del percorso, dove si
 * vedono e non danno fastidio.
 */
export function ordinaGrafo(nodi: Node<DatiNodo>[], archi: Edge[]): Map<string, XYPosition> {
  const esistenti = new Set(nodi.map((n) => n.id));
  const pesoUscita = (a: Edge) => (RAMI_A_DESTRA.has(a.sourceHandle ?? '') ? 1 : 0);

  const uscite = new Map<string, string[]>();
  for (const a of [...archi].sort((x, y) => pesoUscita(x) - pesoUscita(y))) {
    if (!esistenti.has(a.source) || !esistenti.has(a.target)) continue;
    uscite.set(a.source, [...(uscite.get(a.source) ?? []), a.target]);
  }

  // Prima il percorso vero, poi i pezzi staccati: prima chi non ha frecce in
  // arrivo (l'inizio di un pezzo), poi quello che resta.
  const conFrecciaInArrivo = new Set(archi.map((a) => a.target));
  const radici = [
    ...nodi.filter((n) => n.id === ID_INIZIO),
    ...nodi.filter((n) => n.id !== ID_INIZIO && !conFrecciaInArrivo.has(n.id)),
    ...nodi.filter((n) => n.id !== ID_INIZIO),
  ].map((n) => n.id);

  // 1. Le frecce che tornano indietro non contano per l'ordine: senza di
  //    loro il percorso va solo verso il basso e non gira all'infinito.
  const avanti = new Map<string, string[]>();
  const aperti = new Set<string>();
  const visti = new Set<string>();
  const chiusura: string[] = [];
  const esplora = (id: string) => {
    visti.add(id);
    aperti.add(id);
    const proprie: string[] = [];
    for (const f of uscite.get(id) ?? []) {
      if (aperti.has(f)) continue;
      proprie.push(f);
      if (!visti.has(f)) esplora(f);
    }
    avanti.set(id, proprie);
    aperti.delete(id);
    chiusura.push(id);
  };
  for (const r of radici) if (!visti.has(r)) esplora(r);

  // 2. L'altezza: il percorso piu' lungo per arrivarci.
  const livello = new Map<string, number>();
  for (const id of [...chiusura].reverse()) {
    const proprio = livello.get(id) ?? 0;
    livello.set(id, proprio);
    for (const f of avanti.get(id) ?? []) {
      livello.set(f, Math.max(livello.get(f) ?? 0, proprio + 1));
    }
  }

  // 3. La colonna: ogni casella appartiene a chi la raggiunge per primo,
  //    guardando un livello alla volta.
  const figli = new Map<string, string[]>();
  const presi = new Set<string>();
  const inizi: string[] = [];
  for (const r of radici) {
    if (presi.has(r)) continue;
    presi.add(r);
    inizi.push(r);
    const coda = [r];
    while (coda.length > 0) {
      const id = coda.shift() as string;
      const propri: string[] = [];
      for (const f of avanti.get(id) ?? []) {
        if (presi.has(f)) continue;
        presi.add(f);
        propri.push(f);
        coda.push(f);
      }
      figli.set(id, propri);
    }
  }

  const larghezza = new Map<string, number>();
  const misura = (id: string): number => {
    const totale = (figli.get(id) ?? []).reduce((somma, f) => somma + misura(f), 0);
    const propria = Math.max(1, totale);
    larghezza.set(id, propria);
    return propria;
  };

  const posizioni = new Map<string, XYPosition>();
  const piazza = (id: string, colonna: number) => {
    const centro = (colonna + (larghezza.get(id) ?? 1) / 2) * PASSO_ORIZZONTALE;
    posizioni.set(id, {
      x: centro - LARGHEZZA_NODO / 2,
      y: (livello.get(id) ?? 0) * PASSO_VERTICALE,
    });
    let prossima = colonna;
    for (const f of figli.get(id) ?? []) {
      piazza(f, prossima);
      prossima += larghezza.get(f) ?? 1;
    }
  };

  let colonna = 0;
  for (const inizio of inizi) {
    const occupate = misura(inizio);
    piazza(inizio, colonna);
    // Una colonna vuota fra il percorso e i pezzi staccati.
    colonna += occupate + (inizio === ID_INIZIO ? 1 : 0);
  }

  return posizioni;
}
